import { SystemEntity } from '@erp/shared-types';
import { T } from 'gt-react';
import { cn } from '@/lib/utils';
import { getEntityIcon, getEntityDisplayName } from '../utils/index';
import { SupplierPreview } from './supplier-preview';
import { PurchaseOrderPreview } from './purchase-order-preview';
import { PurchaseOrderLineItemPreview } from './purchase-order-line-item-preview';
import { PaymentPreview } from './payment-preview';
import { FinancialTransactionPreview } from './financial-transaction-preview';
import { APInvoicePreview } from './ap-invoice-preview';
import { OutboundEmailPreview } from './outbound-email-preview';
import { ChargePreview } from './charge-preview';
import { ShipmentPreview } from './shipment-preview';
import { SalesOrderPreview } from './sales-order-preview';
import { InboundEmailPreview } from './inbound-email-preview';
import { ExternalSystemReferencePreview } from './external-system-reference-preview';
import { CoreDocumentPreview } from './core-document-preview';
import { CoreDocumentOcrResultPreview } from './core-document-ocr-result-preview';
import { APInvoiceExtractionResultPreview } from './ap-invoice-extraction-result-preview';
import { TransferOrderPreview } from './transfer-order-preview';

interface SystemEntityPreviewProps {
  entityType: SystemEntity;
  entityId: string;
  className?: string;
}

function FallbackPreview({ entityType, className }: SystemEntityPreviewProps) {
  const Icon = getEntityIcon(entityType);
  const displayName = getEntityDisplayName(entityType);

  return (
    <div className={cn('min-w-64 max-w-sm p-3', className)}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="size-4 text-muted-foreground" />
        <h4 className="text-sm font-semibold">{displayName}</h4>
      </div>
      <p className="text-xs text-muted-foreground">
        <T>No preview is available at this time</T>
      </p>
    </div>
  );
}

/**
 * Component renderers for each system entity type
 * Maps entity types to their preview UI representations
 */
const entityRenderers: Partial<
  Record<SystemEntity, (props: SystemEntityPreviewProps) => React.ReactNode>
> = {
  [SystemEntity.AP_SUPPLIER]: (props) => <SupplierPreview {...props} />,
  [SystemEntity.INTERNAL_PURCHASE_ORDER]: (props) => (
    <PurchaseOrderPreview {...props} />
  ),
  [SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM]: (props) => (
    <PurchaseOrderLineItemPreview {...props} />
  ),
  [SystemEntity.PAYMENT]: (props) => <PaymentPreview {...props} />,
  [SystemEntity.FINANCIAL_TRANSACTION]: (props) => (
    <FinancialTransactionPreview {...props} />
  ),
  [SystemEntity.AP_INVOICE]: (props) => <APInvoicePreview {...props} />,
  [SystemEntity.OUTBOUND_EMAIL]: (props) => <OutboundEmailPreview {...props} />,
  [SystemEntity.CHARGE]: ({ entityId, className }) => (
    <ChargePreview entityId={entityId} className={className} />
  ),
  [SystemEntity.SCM_SHIPMENT]: (props) => <ShipmentPreview {...props} />,
  [SystemEntity.AR_SALES_ORDER]: (props) => <SalesOrderPreview {...props} />,
  [SystemEntity.INBOUND_EMAIL]: (props) => <InboundEmailPreview {...props} />,
  [SystemEntity.EXTERNAL_SYSTEM_REFERENCE]: (props) => (
    <ExternalSystemReferencePreview {...props} />
  ),
  [SystemEntity.CORE_DOCUMENT]: (props) => <CoreDocumentPreview {...props} />,
  [SystemEntity.CORE_DOCUMENT_OCR_RESULT]: (props) => (
    <CoreDocumentOcrResultPreview {...props} />
  ),
  // TODO: AI_EXTRACTION_RESULT is a generic entity but we currently only have
  // one concrete producer (AP invoice extraction), so we hardcode that
  // preview. Once more extraction schemas exist, dispatch on the row's
  // schema-definition ref instead of mapping the whole SystemEntity here.
  [SystemEntity.AI_EXTRACTION_RESULT]: (props) => (
    <APInvoiceExtractionResultPreview {...props} />
  ),
  [SystemEntity.IM_TRANSFER_ORDER]: (props) => (
    <TransferOrderPreview {...props} />
  ),
};

/**
 * Preview UI component for system entities
 * Renders a card-based preview for use in hover states
 * Designed to be used with HoverCard from @radix-ui
 */
export function SystemEntityPreview({
  entityType,
  entityId,
  className,
}: SystemEntityPreviewProps) {
  const normalizedEntityType = entityType as SystemEntity;
  const Renderer = entityRenderers[normalizedEntityType];

  if (Renderer) {
    return (
      <>{Renderer({ entityType: normalizedEntityType, entityId, className })}</>
    );
  }

  return (
    <FallbackPreview
      entityType={normalizedEntityType}
      entityId={entityId}
      className={className}
    />
  );
}
